import { ai } from '@/services/ai/ai-service'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import toast from 'react-hot-toast'
import moment from 'moment'

const SLUGS = [
  [
    'process-block',
    'process-block:letter',
    'process-block:policy',
    'process-block:contract',
    'process-block:proposal',
    'process-block:generic',
  ],
  ['risk-card-elaboration', 'risk-card-elaboration:simplify', 'risk-card-elaboration:elaborate-more'],
  [
    'inline-text-enhancements',
    'inline-text-enhancements:make_shorter',
    'inline-text-enhancements:tone_professional',
    'inline-text-enhancements:tone_casual',
    'inline-text-enhancements:tone_straightforward',
    'inline-text-enhancements:tone_confident',
    'inline-text-enhancements:tone_friendly',
    'inline-text-enhancements:simplify_language',
    'inline-text-enhancements:make_longer',
  ],
  ['preprocess-block'],
  ['preprocess-document'],
  ['apply-improvement'],
  ['detect-parties'],
]

const formatSlugName = (slug: string) => {
  return slug
    .split(/[-:]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function SystemPromptsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [systemPrompt, setSystemPrompt] = useState<string>('')
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const selectedSlug = searchParams.get('slug')

  function handleSlugSelect(slug: string) {
    setSearchParams({ slug })
  }

  function handleBack() {
    setSearchParams({})
    setSystemPrompt('')
    setUpdatedAt(null)
  }

  function handleSave() {
    if (!selectedSlug) return

    setIsSaving(true)
    ai.updateSystemPrompt({ slug: selectedSlug, body: systemPrompt })
      .then(() => {
        toast.success('System prompt updated successfully!')
        setUpdatedAt(new Date().toISOString())
      })
      .catch(error => {
        console.error('Error updating system prompt:', error)
        toast.error('Failed to update system prompt')
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  useEffect(() => {
    if (selectedSlug) {
      setIsLoading(true)
      ai.getSystemPrompt(selectedSlug)
        .then(res => {
          setSystemPrompt(res.body || '')
          setUpdatedAt(res.updated_at || null)
        })
        .catch(error => {
          console.error('Error fetching system prompt:', error)
          alert('Failed to fetch system prompt')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [selectedSlug])

  if (selectedSlug) {
    return (
      <>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-3xl font-bold">{formatSlugName(selectedSlug)}</h1>
            <p className="text-muted-foreground">Edit the system prompt for this AI action</p>
          </div>
          <Button variant="outline" onClick={handleBack}>
            ← Back
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            {isLoading ? (
              <div className="py-12 text-center">Loading...</div>
            ) : (
              <>
                <textarea
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  className="min-h-[500px] w-full resize-y rounded-lg border p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  placeholder="Enter system prompt..."
                />
                <div className="mt-4 flex flex-row-reverse items-center justify-between">
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleBack}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {systemPrompt.length} characters · {systemPrompt.trim().split(/\s+/).filter(Boolean).length} words ·
                    Updated {updatedAt ? moment(updatedAt).fromNow() : 'Never'}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </>
    )
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold">System Prompts</h1>
        <p className="text-muted-foreground">Select a system prompt to edit</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {SLUGS.map((group, groupIndex) => {
          const mainSlug = group[0]
          const secondarySlugs = group.slice(1)

          return (
            <Card
              key={groupIndex}
              className="flex h-full flex-col border-2 border-primary/20 transition-all hover:shadow-lg"
            >
              {/* Main Prompt - Eye-catching */}
              <div
                className="cursor-pointer rounded-t-lg bg-gradient-to-br from-primary/5 to-transparent transition-all hover:from-primary/10"
                onClick={() => handleSlugSelect(mainSlug)}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{formatSlugName(mainSlug)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button className="w-full font-semibold">Edit Main Prompt →</Button>
                </CardContent>
              </div>

              {/* Secondary Prompts - Compact buttons */}
              {secondarySlugs.length > 0 && (
                <CardContent className="flex flex-1 flex-col justify-center space-y-2 border-t bg-muted/30 py-3">
                  {secondarySlugs.map(slug => {
                    const subLabel = slug.split(':')[1] || slug
                    return (
                      <Button
                        key={slug}
                        variant="outline"
                        size="sm"
                        className="w-full text-xs font-normal"
                        onClick={() => handleSlugSelect(slug)}
                      >
                        {formatSlugName(subLabel)}
                      </Button>
                    )
                  })}
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>
    </>
  )
}
